import {
  PieceMetadataModel,
  PieceMetadataModelSummary,
} from '@activepieces/pieces-framework';
import {
  AppConnectionScope,
  AppConnectionStatus,
  AppConnectionWithoutSensitiveData,
  PieceAction,
  PieceTrigger,
  isNil,
} from '@activepieces/shared';
import { t } from 'i18next';
import { Plus, Globe, Loader2, RefreshCcw } from 'lucide-react';
import { memo, useState, useEffect } from 'react';
import { ControllerRenderProps, useFormContext } from 'react-hook-form';
import { appConnectionsApi } from '@/features/connections/lib/app-connections-api';
import { useToast } from '@/components/ui/use-toast';

import { appConnectionsHooks } from '../../../../features/connections/lib/app-connections-hooks';
import { useBuilderStateContext } from '../../builder-hooks';

import { AutoFormFieldWrapper } from '@/app/builder/piece-properties/auto-form-field-wrapper';
import { CreateOrEditConnectionDialog } from '@/app/connections/create-edit-connection-dialog';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form';
import {
  Select,
  SelectAction,
  SelectContent,
  SelectItem,
  SelectLoader,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SelectUtilButton } from '@/components/custom/select-util-button';
import { tenantHooks } from '@/hooks/tenant-hooks';

type ConnectionSelectProps = {
  disabled: boolean;
  piece: PieceMetadataModelSummary | PieceMetadataModel;
  isTrigger: boolean;
};

type ConnectionBracketString = `{{connections['${string}']}}`;

const addBrackets = (str: string): ConnectionBracketString =>
  `{{connections['${str}']}}`;
const removeBrackets = (str: string | undefined) => {
  if (isNil(str)) {
    return undefined;
  }
  return str.replace(
    /\{\{connections\['(.*?)'\]\}\}/g,
    (_, connectionName) => connectionName,
  );
};
const ConnectionSelect = memo((params: ConnectionSelectProps) => {
  const { toast } = useToast();
  const { connectionDialogOpen, setConnectionDialogOpen, notifyConnectionCreated } = useBuilderStateContext((state) => ({
    connectionDialogOpen: state.connectionDialogOpen,
    setConnectionDialogOpen: state.setConnectionDialogOpen,
    notifyConnectionCreated: state.notifyConnectionCreated,
  }));
  const [selectConnectionOpen, setSelectConnectionOpen] = useState(false);
  const [reconnectConnection, setReconnectConnection] =
    useState<AppConnectionWithoutSensitiveData | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const form = useFormContext<PieceAction | PieceTrigger>();
  // The admin flow viewer inspects another tenant's flow: an operator who also
  // holds a Khub session would otherwise see their own connections listed here.
  const isInspectingOtherTenant = tenantHooks.useIsInspectingOtherTenant();
  const {
    data: connectionsPage,
    isLoading,
    refetch,
  } = appConnectionsHooks.useConnections(
    {
      pieceName: params.piece.name,
      cursor: undefined,
      limit: 100,
    },
    { enabled: !isInspectingOtherTenant },
  );

  const authValue = form.watch('settings.input.auth');

  // The list is not fetched in the admin flow viewer: fall back to the raw
  // external id so support can still read which connection the step uses.
  const unlistedConnectionName =
    isInspectingOtherTenant && !isNil(authValue)
      ? removeBrackets(authValue)
      : null;

  const selectedConnection = connectionsPage?.find(
    (connection) => connection.externalId === removeBrackets(authValue ?? ''),
  );

  const isGlobalConnection =
    selectedConnection?.scope === AppConnectionScope.PLATFORM;

  useEffect(() => {
    // Only an ACTIVE connection may be bound silently: auto-selecting an
    // expired one bypasses the assistant's connection gate with a dead auth
    const activeConnections = connectionsPage?.filter(
      (connection) => connection.status === AppConnectionStatus.ACTIVE,
    );
    if (activeConnections?.length && !authValue) {
      const connectionAuthId: ConnectionBracketString = addBrackets(
        activeConnections[0].externalId,
      );
      form.setValue('settings.input.auth', connectionAuthId);
    }
  }, [connectionsPage, authValue, form]);

  return (
    <FormField
      control={form.control}
      key={form.getValues().settings.input.auth}
      name={'settings.input.auth'}
      render={({ field }) => (
        <>
          {isLoading && (
            <Select disabled={params.disabled}>
              <SelectContent>
                <SelectLoader />
              </SelectContent>
            </Select>
          )}
          {!isLoading && (
            <AutoFormFieldWrapper
              property={params.piece.auth!}
              propertyName="auth"
              field={field as unknown as ControllerRenderProps}
              disabled={params.disabled}
              hideDescription={true}
              inputName="settings.input.auth"
              allowDynamicValues={!params.isTrigger}
            >
              {!params.disabled && (
                <CreateOrEditConnectionDialog
                  reconnectConnection={reconnectConnection}
                  isGlobalConnection={isGlobalConnection}
                  predefinedConnectionName={null}
                  piece={params.piece}
                  onConnectionCreated={(connection) => {
                    refetch();
                    field.onChange(addBrackets(connection.externalId));
                    notifyConnectionCreated();
                  }}
                  key={`CreateOrEditConnectionDialog-open-${connectionDialogOpen}`}
                  open={connectionDialogOpen}
                  setOpen={setConnectionDialogOpen}
                />
              )}
              <Select
                open={selectConnectionOpen}
                onOpenChange={setSelectConnectionOpen}
                defaultValue={field.value as string | undefined}
                onValueChange={field.onChange}
                disabled={params.disabled}
              >
                <div className="relative">
                  {field.value && !field.disabled && (
                    <>
                      {connectionsPage?.find(
                        (connection) =>
                          connection.externalId ===
                            removeBrackets(field.value) &&
                          connection.scope !== AppConnectionScope.PLATFORM,
                      ) && (
                        <>
                          {isRefreshing ? (
                            <Button
                              variant="ghost"
                              size="xs"
                              className="z-50 absolute right-8 top-2"
                              disabled
                            >
                              <Loader2 className="h-4 w-4 animate-spin" />
                            </Button>
                          ) : (
                            <div className="z-50 absolute right-8 top-2">
                              <SelectUtilButton
                                Icon={RefreshCcw}
                                tooltipText={t('Reconnect')}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (selectedConnection) {
                                    setIsRefreshing(true);
                                    appConnectionsApi.refresh(selectedConnection.externalId).then(() => {
                                      refetch();
                                      toast({
                                        variant: 'success',
                                        title: t('Success'),
                                        description: t('Connection refreshed successfully'),
                                      });
                                    }).finally(() => {
                                      setIsRefreshing(false);
                                    });
                                  }
                                  setSelectConnectionOpen(false);
                                }}
                              />
                            </div>
                          )}
                        </>
                      )}
                    </>
                  )}

                  <SelectTrigger className="flex gap-2 items-center">
                    <SelectValue
                      className="truncate flex-grow flex-shrink"
                      placeholder={t('Select a connection')}
                    >
                      {!isNil(field.value) &&
                      !isNil(
                        connectionsPage?.find(
                          (connection) =>
                            connection.externalId ===
                            removeBrackets(field.value),
                        ),
                      ) ? (
                        <div className="truncate flex-grow flex-shrink flex items-center gap-2">
                          {connectionsPage?.find(
                            (connection) =>
                              connection.externalId ===
                              removeBrackets(field.value),
                          )?.scope === AppConnectionScope.PLATFORM && (
                            <Globe size={16} className="shrink-0" />
                          )}
                          {
                            connectionsPage?.find(
                              (connection) =>
                                connection.externalId ===
                                removeBrackets(field.value),
                            )?.displayName
                          }
                        </div>
                      ) : (
                        unlistedConnectionName && (
                          <div className="truncate flex-grow flex-shrink">
                            {unlistedConnectionName}
                          </div>
                        )
                      )}
                    </SelectValue>
                    <div className="grow"></div>
                    {field.value &&
                      connectionsPage?.find(
                        (connection) =>
                          connection.externalId ===
                            removeBrackets(field.value) &&
                          connection.scope !== AppConnectionScope.PLATFORM,
                      ) && (
                        <Button
                          variant="ghost"
                          size="xs"
                          className="z-50 opacity-0 pointer-events-none"
                        >
                          {t('Reconnect')}
                        </Button>
                      )}
                  </SelectTrigger>
                </div>

                <SelectContent>
                  <SelectAction
                    onClick={() => {
                      setSelectConnectionOpen(false);
                      setReconnectConnection(null);
                      setConnectionDialogOpen(true);
                    }}
                  >
                    <span className="flex items-center gap-1 text-primary w-full">
                      <Plus size={16} />
                      {t('Create Connection')}
                    </span>
                  </SelectAction>

                  {connectionsPage &&
                    connectionsPage.map((connection) => {
                      return (
                        <SelectItem
                          value={addBrackets(connection.externalId)}
                          key={connection.externalId}
                        >
                          <div className="flex items-center gap-2">
                            {connection.scope ===
                              AppConnectionScope.PLATFORM && (
                              <Globe size={16} className="shrink-0" />
                            )}
                            {connection.displayName}
                          </div>
                        </SelectItem>
                      );
                    })}
                </SelectContent>
              </Select>
            </AutoFormFieldWrapper>
          )}
        </>
      )}
    ></FormField>
  );
});

ConnectionSelect.displayName = 'ConnectionSelect';
export { ConnectionSelect };
